import { c as _c } from "react/compiler-runtime";
import { MutationOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocale, useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { CartAndLineItemGroups, CommerceToolsCart, LineItem, LineItemGroup, ShippingMethodsResponse } from '@/types/commercetools';
import { activePromoCodeAtom, isPromoExpiredAtom, promoConfigAtom } from '@/atoms/promoAtom';
import { basketAtom } from '@/store/atoms';
import { canApplyPromo } from '@/utility/canApplyPromo';
import { createNewCart, getCartIdFromStorage, migrateAndFetchCart } from '@/utility/cartHelpers';
import { fetchShippingMethods } from '@/utility/fetchShippingMethods';
import { getActivePromoCode, isUrlCoupon } from '@/utility/promos';
import { post, put, remove } from '@/utility/requestHelpers/clientRequestHelpers';
import { showMiniBasketErrorToast } from '@/utility/toasts/miniBasketErrorToast';
import { trackCartAction, trackCartAddProduct, trackCartDeleteGroup } from '@/utility/tracking/trackCartAction';
import { CartFeatureDetail } from '@/utility/tracking/types';
import { useCurrency } from './useCurrency';
export interface CartItem {
  productTypeId: number;
  productId?: number;
  shopId: number;
  sizeId: string;
  appearanceId: string;
  quantity: number;
  sellableId?: string;
  articleId?: string;
  articleName?: string;
  price: number;
  channel?: string;
  editLink: string;
  /**
   * @deprecated use the trackingData object instead.
   */
  compositions?: CreateOmatDesign[];
  trackingData?: object;
}
export function useCart() {
  const $ = _c(2);
  const locale = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = {
      queryKey: ["cart", locale],
      queryFn: () => migrateAndFetchCart(locale),
      staleTime: 600000,
      refetchOnReconnect: true,
      refetchOnWindowFocus: "always",
      retry: 3,
      placeholderData: _temp
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useQuery(t0);
}
function _temp(previousData) {
  return previousData;
}
export function useShippingMethods(cartId, countryCode) {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== cartId || $[1] !== countryCode) {
    t0 = ["shippingMethods", cartId, countryCode];
    t1 = () => fetchShippingMethods(cartId, countryCode);
    $[0] = cartId;
    $[1] = countryCode;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = !!cartId && !!countryCode;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 3600000
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
function useCartMutation(t0) {
  const $ = _c(19);
  let mutationFn;
  let onError;
  let onSettled;
  let options;
  let t1;
  if ($[0] !== t0) {
    ({
      mutationFn,
      onSettled,
      onError,
      suppressErrorToast: t1,
      ...options
    } = t0);
    $[0] = t0;
    $[1] = mutationFn;
    $[2] = onError;
    $[3] = onSettled;
    $[4] = options;
    $[5] = t1;
  } else {
    mutationFn = $[1];
    onError = $[2];
    onSettled = $[3];
    options = $[4];
    t1 = $[5];
  }
  const suppressErrorToast = t1 === undefined ? false : t1;
  const locale = useLocale();
  const t = useTranslations();
  const queryClient = useQueryClient();
  let t2;
  if ($[6] !== locale || $[7] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: ["cart", locale]
      });
    };
    $[6] = locale;
    $[7] = queryClient;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const onSettledFallback = t2;
  let t3;
  if ($[9] !== onError || $[10] !== suppressErrorToast || $[11] !== t) {
    t3 = (error, variables, context, mutation) => {
      if (!suppressErrorToast) {
        showMiniBasketErrorToast(t("miniBasket.errorToast"));
      }
      onError?.(error, variables, context, mutation as never);
    };
    $[9] = onError;
    $[10] = suppressErrorToast;
    $[11] = t;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const onErrorFallback = t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["cart-action"];
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = onSettled ?? onSettledFallback;
  let t6;
  if ($[14] !== mutationFn || $[15] !== onErrorFallback || $[16] !== options || $[17] !== t5) {
    t6 = {
      ...options,
      mutationKey: t4,
      mutationFn,
      onError: onErrorFallback,
      onSettled: t5
    };
    $[14] = mutationFn;
    $[15] = onErrorFallback;
    $[16] = options;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return useMutation(t6);
}
type MutateParams = {
  lineItem: LineItem;
  payload: {
    quantity: number;
    previousQuantity: number;
  };
};
export function useUpdateBasket(featureDetail) {
  const $ = _c(10);
  const {
    data: currency
  } = useCurrency();
  const locale = useLocale();
  const setOpenBasket = useSetAtom(basketAtom);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== currency || $[1] !== locale || $[2] !== queryClient) {
    t0 = async t1 => {
      const {
        cartItems,
        removeIds
      } = t1;
      if (removeIds?.length) {
        const cartId = getCartIdFromStorage();
        if (!cartId) {
          throw new Error("Cart ID not found in local storage.");
        }
        const cartData = await queryClient.ensureQueryData({
          queryKey: ["cart", locale],
          queryFn: () => migrateAndFetchCart(locale)
        });
        if (!cartData) {
          throw new Error("Cart data not available.");
        }
        const lineItemIds = cartData.cart.lineItems.filter(lineItem => removeIds.includes(String(lineItem.custom.fields.sprdProductId))).map(_temp2);
        if (lineItemIds.length) {
          await post(`/api/v1/carts/${cartId}/items/delete`, {
            lineItemIds
          });
        }
      }
      if (cartItems?.length) {
        const cartId_0 = getCartIdFromStorage();
        if (cartId_0) {
          return await post(`/api/v1/carts/${cartId_0}/items`, {
            cartItems
          });
        }
        return await createNewCart(locale, currency, cartItems);
      }
    };
    $[0] = currency;
    $[1] = locale;
    $[2] = queryClient;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== featureDetail || $[5] !== setOpenBasket) {
    t1 = (_, t2) => {
      const {
        cartItems: cartItems_0,
        showBasket
      } = t2;
      if (cartItems_0?.length) {
        trackCartAddProduct(featureDetail, cartItems_0);
      }
      if (showBasket) {
        setOpenBasket(true);
      }
    };
    $[4] = featureDetail;
    $[5] = setOpenBasket;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useCartMutation(t2);
}
function _temp2(lineItem_0) {
  return lineItem_0.id;
}
export function useAddItem(featureDetail) {
  const $ = _c(8);
  const {
    data: currency
  } = useCurrency();
  const locale = useLocale();
  let t0;
  if ($[0] !== currency || $[1] !== locale) {
    t0 = async t1 => {
      const {
        cartItems
      } = t1;
      const cartId = getCartIdFromStorage();
      if (cartId) {
        return await post(`/api/v1/carts/${cartId}/items`, {
          cartItems
        });
      }
      return await createNewCart(locale, currency, cartItems);
    };
    $[0] = currency;
    $[1] = locale;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== featureDetail) {
    t1 = (_, t2) => {
      const {
        cartItems: cartItems_0
      } = t2;
      return trackCartAddProduct(featureDetail, cartItems_0);
    };
    $[3] = featureDetail;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useCartMutation(t2);
}
export function useUpdateQuantity(lineItemGroup) {
  const $ = _c(2);
  let t0;
  if ($[0] !== lineItemGroup) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: (_, t1) => {
        const {
          lineItem: lineItem_0,
          payload: payload_0
        } = t1;
        const quantityDiff = payload_0.previousQuantity - payload_0.quantity;
        const trackingAction = quantityDiff > 0 ? "cart-removal" : "cart-addition";
        trackCartAction(trackingAction, lineItemGroup, lineItem_0, Math.abs(quantityDiff));
      }
    };
    $[0] = lineItemGroup;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useCartMutation(t0);
}
async function _temp3(t0) {
  const {
    lineItem,
    payload
  } = t0;
  const cartId = getCartIdFromStorage();
  if (!cartId) {
    throw new Error("Cart ID not found in local storage.");
  }
  return await put(`/api/v1/carts/${cartId}/items/${lineItem.id}`, {
    quantity: payload.quantity
  });
}
export function useRemoveItem(lineItemGroup) {
  const $ = _c(2);
  let t0;
  if ($[0] !== lineItemGroup) {
    t0 = {
      mutationFn: _temp4,
      onSuccess: (_, t1) => {
        const {
          lineItem: lineItem_0
        } = t1;
        trackCartAction("cart-removal", lineItemGroup, lineItem_0);
      }
    };
    $[0] = lineItemGroup;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useCartMutation(t0);
}
async function _temp4(t0) {
  const {
    lineItem
  } = t0;
  const cartId = getCartIdFromStorage();
  if (!cartId) {
    throw new Error("Cart ID not found in local storage.");
  }
  return await remove(`/api/v1/carts/${cartId}/items/${lineItem.id}`);
}
export function useRemoveItemGroup() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp6,
      onSuccess: _temp7
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useCartMutation(t0);
}
function _temp7(_, t0) {
  const {
    lineItemGroup: lineItemGroup_0
  } = t0;
  return trackCartDeleteGroup(lineItemGroup_0);
}
async function _temp6(t0) {
  const {
    lineItemGroup
  } = t0;
  const cartId = getCartIdFromStorage();
  if (!cartId) {
    throw new Error("Cart ID not found in local storage.");
  }
  return await post(`/api/v1/carts/${cartId}/items/delete`, {
    lineItemIds: lineItemGroup.lineItems.map(_temp5)
  });
}
function _temp5(lineItem) {
  return lineItem.id;
}
export function useAddVoucher(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    suppressErrorToast: t2
  } = t1;
  const suppressErrorToast = t2 === undefined ? false : t2;
  let t3;
  if ($[2] !== suppressErrorToast) {
    t3 = {
      mutationFn: _temp8,
      retry: 3,
      suppressErrorToast
    };
    $[2] = suppressErrorToast;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return useCartMutation(t3);
}
async function _temp8(t0) {
  const {
    coupon
  } = t0;
  const cartId = getCartIdFromStorage();
  if (!cartId) {
    throw new Error("Cart ID not found in local storage");
  }
  const payload = {
    coupons: [coupon]
  };
  return await post(`/api/v1/carts/${cartId}/coupons`, payload);
}
export const useApplyVoucher = () => {
  const $ = _c(5);
  const {
    mutateAsync
  } = useAddVoucher();
  const {
    data: cartAndLineItemGroups
  } = useCart();
  const promoConfig = useAtomValue(promoConfigAtom);
  const activePromoCode = useAtomValue(activePromoCodeAtom);
  let t0;
  if ($[0] !== activePromoCode || $[1] !== cartAndLineItemGroups || $[2] !== mutateAsync || $[3] !== promoConfig) {
    t0 = async () => {
      if (activePromoCode?.fromUrl) {
        return Promise.resolve();
      }
      const canApplyVoucher = canApplyPromo(cartAndLineItemGroups, promoConfig, activePromoCode);
      if (!canApplyVoucher || !activePromoCode) {
        return Promise.resolve();
      }
      return mutateAsync({
        coupon: activePromoCode.code
      });
    };
    $[0] = activePromoCode;
    $[1] = cartAndLineItemGroups;
    $[2] = mutateAsync;
    $[3] = promoConfig;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
};
export const useAutoApplyVoucher = () => {
  const $ = _c(3);
  const applyVoucher = useApplyVoucher();
  let t0;
  let t1;
  if ($[0] !== applyVoucher) {
    t0 = () => {
      applyVoucher();
    };
    t1 = [applyVoucher];
    $[0] = applyVoucher;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
};
export function useActivateVoucher() {
  const $ = _c(5);
  const currentPromo = useAtomValue(promoConfigAtom);
  const setActivePromoCode = useSetAtom(activePromoCodeAtom);
  let t0;
  if ($[0] !== currentPromo || $[1] !== setActivePromoCode) {
    t0 = () => {
      if (!currentPromo) {
        return;
      }
      const code = currentPromo.content["promo-tool"].campaignDetails.code;
      setActivePromoCode({
        code,
        expires: currentPromo.content._meta.lifecycle ? new Date(currentPromo.content._meta.lifecycle?.expiryTime) : null
      });
    };
    $[0] = currentPromo;
    $[1] = setActivePromoCode;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const applyVoucher = t0;
  let t1;
  if ($[3] !== applyVoucher) {
    t1 = {
      applyVoucher
    };
    $[3] = applyVoucher;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
export function useAutoActivateVoucherFromUrl() {
  const $ = _c(5);
  const activePromoCode = useAtomValue(activePromoCodeAtom);
  const setActivePromoCode = useSetAtom(activePromoCodeAtom);
  const searchParams = useSearchParams();
  let t0;
  let t1;
  if ($[0] !== activePromoCode || $[1] !== searchParams || $[2] !== setActivePromoCode) {
    t0 = () => {
      const couponFromUrl = searchParams.get("couponCode");
      if (!couponFromUrl) {
        return;
      }
      if (activePromoCode?.code === couponFromUrl && activePromoCode.fromUrl) {
        return;
      }
      setActivePromoCode({
        code: couponFromUrl,
        expires: null,
        fromUrl: true
      });
    };
    t1 = [searchParams, activePromoCode, setActivePromoCode];
    $[0] = activePromoCode;
    $[1] = searchParams;
    $[2] = setActivePromoCode;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}

/**
 * Activates the current CMS campaign's coupon on page load when the campaign has
 * `autoApplyCoupon` flag enabled, reusing the `?couponCode=` path.
 */
export function useAutoActivateCampaignCoupon() {
  const $ = _c(10);
  const promoConfig = useAtomValue(promoConfigAtom);
  const isExpired = useAtomValue(isPromoExpiredAtom);
  const setActivePromoCode = useSetAtom(activePromoCodeAtom);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== isExpired || $[1] !== promoConfig?.content || $[2] !== searchParams || $[3] !== setActivePromoCode) {
    t0 = () => {
      if (searchParams.get("couponCode") || isUrlCoupon()) {
        return;
      }
      if (isExpired) {
        return;
      }
      const campaignDetails = promoConfig?.content["promo-tool"].campaignDetails;
      if (!campaignDetails?.autoApplyCoupon || !campaignDetails.code) {
        return;
      }
      if (getActivePromoCode() === campaignDetails.code) {
        return;
      }
      setActivePromoCode({
        code: campaignDetails.code,
        expires: null,
        fromUrl: true
      });
    };
    $[0] = isExpired;
    $[1] = promoConfig?.content;
    $[2] = searchParams;
    $[3] = setActivePromoCode;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== isExpired || $[6] !== promoConfig || $[7] !== searchParams || $[8] !== setActivePromoCode) {
    t1 = [isExpired, promoConfig, searchParams, setActivePromoCode];
    $[5] = isExpired;
    $[6] = promoConfig;
    $[7] = searchParams;
    $[8] = setActivePromoCode;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  useEffect(t0, t1);
}
export const useApplyUrlCoupon = () => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      suppressErrorToast: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    mutateAsync
  } = useAddVoucher(t0);
  const {
    data: cartAndLineItemGroups
  } = useCart();
  const activePromoCode = useAtomValue(activePromoCodeAtom);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const attempted = useRef(t1);
  let t2;
  if ($[2] !== activePromoCode || $[3] !== cartAndLineItemGroups || $[4] !== mutateAsync) {
    t2 = async () => {
      if (!activePromoCode?.fromUrl || !cartAndLineItemGroups) {
        return;
      }
      const {
        code
      } = activePromoCode;
      const attemptKey = `${code}:${cartAndLineItemGroups.cart.version}`;
      const cartCoupons = cartAndLineItemGroups.cart?.custom?.fields?.sprdVoucherList?.split(",") || [];
      if (cartCoupons.includes(code) || attempted.current.has(attemptKey)) {
        return;
      }
      attempted.current.add(attemptKey);
      try {
        await mutateAsync({
          coupon: code
        });
      } catch {}
    };
    $[2] = activePromoCode;
    $[3] = cartAndLineItemGroups;
    $[4] = mutateAsync;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const useAutoApplyUrlCoupon = () => {
  const $ = _c(3);
  const applyUrlCoupon = useApplyUrlCoupon();
  let t0;
  let t1;
  if ($[0] !== applyUrlCoupon) {
    t0 = () => {
      applyUrlCoupon();
    };
    t1 = [applyUrlCoupon];
    $[0] = applyUrlCoupon;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
};